import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Heart, User, ShoppingBag, ShoppingCart, X, UserCircle, LogOut } from 'lucide-react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { toast } from 'sonner';
import Footer from './Footer';

const GenderSelection = () => {
  const { t, API, token, user, setToken, setUser, logout, toggleFavorite, isFavorite, getCartCount, favorites } = useApp();
  const navigate = useNavigate();
  const [newArrivals, setNewArrivals] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);
  const [isPaused, setIsPaused] = useState(false);
  const carouselRef = useRef(null);
  const autoScrollRef = useRef(null);
  
  // Login popup states
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [showLoggedInPopup, setShowLoggedInPopup] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);

  const cartCount = getCartCount();

  const handleFavoriteClick = async (e, productId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!token) {
      setShowLoginPopup(true);
      return;
    }
    await toggleFavorite(productId);
  };

  const handleUserIconClick = () => {
    if (user) {
      setShowLoggedInPopup(true);
    } else {
      setShowLoginPopup(true);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      
      if (response.data.requires_2fa) {
        toast.info('2FA vereist - ga naar de login pagina');
        setShowLoginPopup(false);
        navigate('/login');
        return;
      }
      
      setToken(response.data.token);
      setUser(response.data.user);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      setShowLoginPopup(false);
      setShowLoggedInPopup(true);
      toast.success('Succesvol ingelogd!');
      setLoginForm({ email: '', password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Inloggen mislukt');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleGoToAccount = () => {
    setShowLoggedInPopup(false);
    navigate('/account');
  };

  const handleContinueShopping = () => {
    setShowLoggedInPopup(false);
  };

  const handleLogout = () => {
    logout();
    setShowLoggedInPopup(false);
    toast.success('Uitgelogd');
  };

  useEffect(() => {
    loadNewArrivals();
  }, []);

  // Auto-scroll every 5 seconds
  useEffect(() => {
    if (newArrivals.length === 0 || isPaused) return;

    autoScrollRef.current = setInterval(() => {
      if (!isTransitioning) {
        setIsTransitioning(true);
        setCurrentIndex((prev) => (prev + 1) % newArrivals.length);
        setTimeout(() => setIsTransitioning(false), 400);
      }
    }, 5000);

    return () => {
      if (autoScrollRef.current) {
        clearInterval(autoScrollRef.current);
      }
    };
  }, [newArrivals.length, isPaused, isTransitioning]);

  // Responsive: determine how many items to show based on screen width
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setVisibleCount(2);
      } else if (width < 1024) {
        setVisibleCount(3);
      } else if (width < 1280) {
        setVisibleCount(4);
      } else {
        setVisibleCount(5);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadNewArrivals = async () => {
    try {
      const response = await axios.get(`${API}/products?sort=newest&limit=10`);
      setNewArrivals(response.data.products);
    } catch (error) {
      console.error('Error loading new arrivals:', error);
    }
  };

  const getDisplayItems = useCallback(() => {
    if (newArrivals.length === 0) return [];
    
    const items = [];
    const total = newArrivals.length;
    
    for (let i = 0; i < visibleCount + 2; i++) {
      const index = (currentIndex + i) % total;
      items.push({
        ...newArrivals[index],
        displayIndex: i,
        originalIndex: index
      });
    }
    
    return items;
  }, [newArrivals, currentIndex, visibleCount]);

  const nextSlide = () => {
    if (isTransitioning || newArrivals.length === 0) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev + 1) % newArrivals.length);
    setTimeout(() => setIsTransitioning(false), 400);
  };

  const prevSlide = () => {
    if (isTransitioning || newArrivals.length === 0) return;
    setIsTransitioning(true);
    setCurrentIndex((prev) => (prev - 1 + newArrivals.length) % newArrivals.length);
    setTimeout(() => setIsTransitioning(false), 400);
  };

  const displayItems = getDisplayItems();
  const itemWidth = 100 / visibleCount;

  return (
    <div 
      className="min-h-screen relative flex flex-col"
      data-testid="gender-selection"
    >
      {/* Background */}
      <div 
        className="absolute inset-0"
        style={{ background: 'radial-gradient(circle at center, #2C1B18 0%, #050505 100%)' }}
      />

      {/* Header with Login Icon */}
      <header className="relative z-20 glass-strong">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="font-heading text-2xl"
            data-testid="logo-btn"
          >
            <span className="zuiver-letters-nav">
              {'Zuiver'.split('').map((letter, index) => (
                <span key={index} className="zuiver-letter-nav" style={{ animationDelay: `${index * 0.42}s` }}>
                  {letter}
                </span>
              ))}
            </span>
            <span className="text-white">Parfum</span>
          </button>
          
          {/* Right Icons */}
          <div className="flex items-center gap-3">
            {/* Favorites */}
            <button 
              onClick={() => user ? navigate('/account/favorites') : setShowLoginPopup(true)}
              className="relative p-2 text-white hover:text-[#D4AF37] transition-colors"
              data-testid="nav-favorites"
            >
              <Heart size={22} />
              {favorites.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#D4AF37] text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {favorites.length}
                </span>
              )}
            </button>

            {/* Cart */}
            <button 
              onClick={() => navigate('/cart')}
              className="relative p-2 text-white hover:text-[#D4AF37] transition-colors"
              data-testid="nav-cart"
            >
              <ShoppingBag size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-[#D4AF37] text-black text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                  {cartCount}
                </span>
              )}
            </button>

            {/* User Icon - Gold if logged in, White if not */}
            <button 
              onClick={handleUserIconClick}
              className={`p-2 transition-colors ${user ? 'text-[#D4AF37]' : 'text-white hover:text-[#D4AF37]'}`}
              data-testid="nav-user-icon"
            >
              <User size={22} className={user ? 'fill-[#D4AF37]' : ''} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content - Split Screen */}
      <div className="relative z-10 flex flex-col md:flex-row min-h-[calc(100vh-180px)]">
        {/* Men Section */}
        <button
          onClick={() => navigate('/products/men')}
          className="flex-1 relative group cursor-pointer overflow-hidden"
          data-testid="men-section-btn"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1519761192872-d8496d4ccaaf?w=1200)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent md:bg-gradient-to-t md:from-black/80 md:via-black/40 md:to-transparent" />
          
          <div className="relative z-10 h-full min-h-[50vh] md:min-h-full flex flex-col items-center justify-center p-8">
            <h2 className="font-heading text-5xl md:text-7xl text-white mb-4 transform transition-transform duration-300 group-hover:-translate-y-2">
              {t('men')}
            </h2>
            <div className="btn-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {t('buy_now')}
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
            <div className="flex items-center gap-3 text-[#D4AF37] bg-black/40 backdrop-blur-sm px-6 py-3 rounded-full border border-[#D4AF37]/30">
              <span className="font-accent text-base md:text-lg tracking-wider">
                {t('perfume_for_men')}
              </span>
            </div>
          </div>

          <div className="absolute right-0 top-1/4 bottom-1/4 w-px bg-gradient-to-b from-transparent via-[#D4AF37]/50 to-transparent hidden md:block" />
        </button>

        {/* Women Section */}
        <button
          onClick={() => navigate('/products/women')}
          className="flex-1 relative group cursor-pointer overflow-hidden"
          data-testid="women-section-btn"
        >
          <div 
            className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/12053223/pexels-photo-12053223.jpeg?w=1200)',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-l from-black/70 via-black/50 to-transparent md:bg-gradient-to-t md:from-black/80 md:via-black/40 md:to-transparent" />
          
          <div className="relative z-10 h-full min-h-[50vh] md:min-h-full flex flex-col items-center justify-center p-8">
            <h2 className="font-heading text-5xl md:text-7xl text-white mb-4 transform transition-transform duration-300 group-hover:-translate-y-2">
              {t('women')}
            </h2>
            <div className="btn-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {t('buy_now')}
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2">
            <div className="flex items-center gap-3 text-[#D4AF37] bg-black/40 backdrop-blur-sm px-6 py-3 rounded-full border border-[#D4AF37]/30">
              <span className="font-accent text-base md:text-lg tracking-wider">
                {t('perfume_for_women')}
              </span>
            </div>
          </div>
        </button>
      </div>

      {/* New Arrivals Carousel */}
      {newArrivals.length > 0 && (
        <div 
          className="relative z-20 glass-strong py-6"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl text-white">
                {t('new_arrivals')}
              </h3>
              <div className="flex gap-2">
                <button 
                  onClick={prevSlide}
                  disabled={isTransitioning}
                  className="p-2 glass rounded-full hover:bg-white/10 transition-colors disabled:opacity-50"
                  data-testid="carousel-prev"
                >
                  <ChevronLeft size={20} className="text-[#D4AF37]" />
                </button>
                <button 
                  onClick={nextSlide}
                  disabled={isTransitioning}
                  className="p-2 glass rounded-full hover:bg-white/10 transition-colors disabled:opacity-50"
                  data-testid="carousel-next"
                >
                  <ChevronRight size={20} className="text-[#D4AF37]" />
                </button>
              </div>
            </div>

            <div className="overflow-hidden" ref={carouselRef}>
              <div 
                className="flex transition-transform duration-400 ease-out"
                style={{ gap: '16px' }}
              >
                {displayItems.map((product, index) => (
                  <div 
                    key={`${product.id}-${index}`}
                    onClick={() => navigate(`/product/${product.id}`)}
                    className="cursor-pointer group relative flex-shrink-0 transition-all duration-400"
                    style={{ width: `calc(${itemWidth}% - ${16 * (visibleCount - 1) / visibleCount}px)` }}
                  >
                    <div className="aspect-square rounded-lg overflow-hidden bg-[#121212] mb-2 relative">
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      <button
                        onClick={(e) => handleFavoriteClick(e, product.id)}
                        className={`absolute top-2 right-2 p-1.5 rounded-full bg-black/50 backdrop-blur-sm hover:bg-black/70 transition-colors ${
                          isFavorite(product.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                        }`}
                        data-testid={`carousel-favorite-btn-${product.id}`}
                      >
                        <Heart 
                          size={16} 
                          className={isFavorite(product.id) ? 'fill-red-500 text-red-500' : 'text-white'} 
                        />
                      </button>
                    </div>
                    <p className="text-[#D4AF37] text-xs uppercase tracking-wider">{product.brand}</p>
                    <p className="text-white text-sm font-heading truncate">{product.name}</p>
                    <p className="text-[#D4AF37] font-bold">€{product.price?.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center gap-2 mt-4">
              {newArrivals.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    if (!isTransitioning) {
                      setIsTransitioning(true);
                      setCurrentIndex(index);
                      setTimeout(() => setIsTransitioning(false), 400);
                    }
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'bg-[#D4AF37] w-6' 
                      : 'bg-[#57534E] hover:bg-[#D4AF37]/50'
                  }`}
                  data-testid={`carousel-dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Pitch Text */}
      <div className="relative z-20 glass-strong py-4 px-6">
        <p className="text-center text-sm md:text-base text-[#A8A29E] max-w-4xl mx-auto font-body">
          {t('pitch')}
        </p>
      </div>

      {/* Footer */}
      <Footer />

      {/* Login Popup */}
      {showLoginPopup && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4">
          <div className="glass rounded-2xl p-6 max-w-md w-full relative">
            <button 
              onClick={() => setShowLoginPopup(false)}
              className="absolute top-4 right-4 text-[#A8A29E] hover:text-white"
            >
              <X size={24} />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <User size={32} className="text-[#D4AF37]" />
              </div>
              <h2 className="text-white text-xl font-heading">{t('login')}</h2>
              <p className="text-[#A8A29E] text-sm mt-1">{t('continue_shopping')}</p>
            </div>

            <form onSubmit={handleLogin} className="space-y-4" data-testid="login-popup-form">
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">{t('email')}</label>
                <input
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({...loginForm, email: e.target.value})}
                  className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                  placeholder="jouw@email.nl"
                  required
                  data-testid="login-popup-email"
                />
              </div>
              <div>
                <label className="block text-[#A8A29E] text-sm mb-2">{t('password')}</label>
                <input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                  className="w-full bg-[#1C1917] border border-[#2C1B18] rounded-lg px-4 py-3 text-white focus:border-[#D4AF37] outline-none"
                  placeholder="••••••••"
                  required
                  data-testid="login-popup-password"
                />
              </div>
              
              <button
                type="submit"
                disabled={loginLoading}
                className="btn-primary w-full flex items-center justify-center"
                data-testid="login-popup-submit"
              >
                {loginLoading ? <div className="spinner"></div> : t('login')}
              </button>
            </form>

            <div className="mt-4 text-center">
              <p className="text-[#A8A29E] text-sm">
                {t('no_account')}{' '}
                <button 
                  onClick={() => { setShowLoginPopup(false); navigate('/register'); }}
                  className="text-[#D4AF37] hover:underline"
                >
                  {t('register')}
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Logged In Popup */}
      {showLoggedInPopup && user && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4">
          <div className="glass rounded-2xl p-6 max-w-md w-full relative">
            <button 
              onClick={() => setShowLoggedInPopup(false)}
              className="absolute top-4 right-4 text-[#A8A29E] hover:text-white"
            >
              <X size={24} />
            </button>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <UserCircle size={40} className="text-[#D4AF37]" />
              </div>
              <h2 className="text-white text-xl font-heading">{t('welcome_back')}</h2>
              <p className="text-[#D4AF37] font-medium mt-1">{user.first_name} {user.last_name}</p>
              <p className="text-[#A8A29E] text-sm">{user.email}</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleGoToAccount}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#D4AF37] hover:bg-[#E5C048] text-black rounded-xl font-medium transition-colors"
                data-testid="logged-in-popup-account"
              >
                <User size={20} />
                {t('go_to_account')}
              </button>
              
              <button
                onClick={handleContinueShopping}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 border border-[#2C1B18] hover:border-[#D4AF37] text-white rounded-xl font-medium transition-colors"
                data-testid="logged-in-popup-continue"
              >
                <ShoppingCart size={20} />
                {t('continue_shopping')}
              </button>
            </div>

            <div className="mt-6 pt-4 border-t border-[#2C1B18]">
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 text-red-500 hover:text-red-400 py-2 transition-colors"
                data-testid="logged-in-popup-logout"
              >
                <LogOut size={18} />
                {t('logout')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenderSelection;
